"use client";

import React, { useEffect, useState } from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, User, Tag, ArrowRight, Clock, Share2 } from "lucide-react";



function PostDate({ date }: { date?: string }) {
  if (!date) return null;
  const dateObj = new Date(date);
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Calendar className="h-4 w-4" />
      <time dateTime={date}>{dateObj.toLocaleDateString("en-US", { year: 'numeric', month: 'long', day: 'numeric' })}</time>
    </div>
  );
}

function PostAuthor({ author }: { author?: any }) {
  if (!author) return null;
  return (
    <div className="flex items-center gap-2 text-sm font-medium text-foreground">
      <User className="h-4 w-4 text-primary" />
      <span>{author.name}</span>
    </div>
  );
}

function PostCategories({ categories }: { categories?: any[] }) {
  if (!categories || categories.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {categories.map((cat, i) => (
        <span key={cat.id} data-index={i} className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
          {cat.name}
        </span>
      ))}
    </div>
  );
}



function RelatedPostsList() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[1, 2, 3].map((_, i) => (
          <div key={i} data-index={i} className="h-64 animate-pulse rounded-xl bg-muted/50" />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return null; 
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => (
        <Card key={post.id} data-index={i} className="flex h-full flex-col overflow-hidden border-border/50 bg-card shadow-sm transition-all hover:shadow-md">
          {post.featuredImage && (
            <div className="aspect-video w-full overflow-hidden bg-muted">
              <img
                src={post.featuredImage}
                alt={post.title}
                className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
              />
            </div>
          )}
          <CardHeader className="p-5 pb-2">
            <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
              <Calendar className="h-3 w-3" />
              <span>{new Date(post.date || "").toLocaleDateString()}</span>
            </div>
            <CardTitle className="line-clamp-2 text-lg font-bold leading-tight text-foreground group-hover:text-primary">
              <Link to={post.link || "#"}>{post.title}</Link>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex-grow p-5 pt-2">
            <div 
              className="line-clamp-3 text-sm text-muted-foreground"
              dangerouslySetInnerHTML={{ __html: post.excerpt || "" }}
            />
          </CardContent>
          <CardFooter className="p-5 pt-0">
            <Link to={post.link || "#"} className="w-full">
              <Button variant="outline" className="w-full justify-between group">
                Read Article
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}



function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="mx-auto max-w-4xl space-y-8 p-6">
        <div className="h-12 w-3/4 animate-pulse rounded-lg bg-muted/50" />
        <div className="h-6 w-1/2 animate-pulse rounded-lg bg-muted/50" />
        <div className="aspect-video w-full animate-pulse rounded-xl bg-muted/50" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded bg-muted/50" />
          <div className="h-4 w-full animate-pulse rounded bg-muted/50" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-muted/50" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <h2 className="text-2xl font-bold text-destructive">Error Loading Post</h2>
        <p className="text-muted-foreground">{error}</p>
        <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <h2 className="text-2xl font-bold">Post Not Found</h2>
        <p className="text-muted-foreground">The content you are looking for does not exist.</p>
        <Link to="/NewsPage" className="mt-4">
          <Button>Back to News</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  
  const relatedQuery = new WP_Query({
    posts_per_page: 3,
    post__not_in: [post.id],
    category__in: post.categories?.map((c) => c.id) || [],
    post_type: "post",
    orderby: "date",
    order: "DESC",
  });

  return (
    <article className="animate-in fade-in slide-in-from-bottom-4 duration-700">
      
      <header className="mx-auto max-w-4xl px-6 pt-12 pb-8 text-center lg:pt-20">
        <div className="mb-6 flex justify-center">
          <PostCategories categories={post.categories} />
        </div>
        
        <h1 className="mb-6 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-6xl">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center justify-center gap-6 text-muted-foreground">
          <PostAuthor author={post.author} />
          <span className="hidden h-1 w-1 rounded-full bg-muted-foreground/50 sm:block" />
          <PostDate date={post.date} />
          
          <span className="hidden h-1 w-1 rounded-full bg-muted-foreground/50 sm:block" />
          <div className="flex items-center gap-2 text-sm">
            <Clock className="h-4 w-4" />
            <span>5 min read</span>
          </div>
        </div>
      </header>

      
      {post.featuredImage && (
        <div className="mx-auto mb-16 max-w-6xl px-4 md:px-6">
          <div className="relative aspect-[21/9] w-full overflow-hidden rounded-2xl shadow-xl">
            <img
              src={post.featuredImage}
              alt={post.title}
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      )}

      
      <div className="mx-auto max-w-2xl px-4 md:px-6">
        {post.author && (
          <div className="py-8">
            <div className="rounded-2xl bg-muted/30 p-8">
              <div className="flex flex-col items-center gap-6 text-center sm:flex-row sm:text-left">
                <Avatar className="h-20 w-20 border-2 border-background shadow-sm">
                  <AvatarImage src={post.author.avatar?.['96']} alt={post.author.name} />
                  <AvatarFallback className="text-lg font-bold text-primary">
                    {post.author.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 space-y-2">
                  <h3 className="text-xl font-bold text-foreground">About {post.author.name}</h3>
                  <p className="text-muted-foreground">
                    Expert contributor at NUCETECH. Specializing in construction technology and digital transformation solutions.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div 
          className="prose prose-lg prose-slate dark:prose-invert max-w-none 
            prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-foreground prose-headings:leading-relaxed
            prose-p:leading-loose prose-p:text-muted-foreground
            prose-a:text-primary prose-a:no-underline hover:prose-a:underline
            prose-strong:font-semibold prose-strong:text-foreground
            prose-img:rounded-xl prose-img:shadow-md
            prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:bg-muted/30 prose-blockquote:py-2 prose-blockquote:pl-4 prose-blockquote:italic"
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />

        
        {post.tags && post.tags.length > 0 && (
          <div className="mt-12 flex flex-wrap items-center gap-3 border-t border-border pt-8">
            <Tag className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">Tags:</span>
            {post.tags.map((tag, i) => (
              <Link key={tag.id} data-index={i} to={tag.link || "#"}>
                <span className="rounded-md bg-muted px-3 py-1 text-sm text-foreground transition-colors hover:bg-primary hover:text-primary-foreground">
                  #{tag.name}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>

      
      <section className="mt-20 bg-muted/20 py-16">
        <div className="mx-auto max-w-6xl px-4 md:px-6">
          <div className="mb-10 flex items-center justify-between">
            <h2 className="text-3xl font-bold tracking-tight text-foreground">Related Articles</h2>
            <Link to="/NewsPage">
              <Button variant="ghost" className="hidden sm:flex">
                View All News <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          
          <PostsProvider wp_query={relatedQuery}>
            <RelatedPostsList />
          </PostsProvider>

          <div className="mt-8 flex justify-center sm:hidden">
            <Link to="/NewsPage">
              <Button variant="outline">View All News</Button>
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}



export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = (typeof window !== 'undefined' && (window as any).wvcClient) 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post", p: 1 }; 

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section id="single-post-template-section" className="bg-background min-h-screen w-full">
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}